import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PagedTable from '../../components/sorted-table/PagedTable';
import { FilterDropdown, Loader } from '../../components';
import ActionMenu from '../../components/action-buttons/ActionButtons';
import { CancelSVG, SearchSVG } from '../../components/svgs';
import DateRangeInput from '../../components/custom-input/DateInputRange';
import PaymentReportDetailsModal from './my-listing/PaymentReportDetailsModal';
import { StandardParams, StandardParamsToODataParams } from '../../types/interfaces/apiParams.interfaces';
import { DateRange, PaymentReportItem, PaymentReportProps, PaymentSummary } from '../../types/interfaces/paymentReport.interface';
import { MarketPlaceSpecimenResponse } from '../../types/interfaces/marketPlace.interfaces';
import { Genders } from '../../types/enums';
import { showToast } from '../../services/toast.service';
import { centsToDollars } from '../../services/account.service';
import {
  getMarketPlacePaymentReportSummary,
  getMarketPlacePaymentReportTransactions,
} from '../../api/marketPlacePaymentTransactionOrderDetailApi';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, MARKETPLACE_MY_ORDERS_LABELS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { dateFilter } from '../../utils/dateHandler';
import GrossSaleSVG from '../../components/svgs/GrossSale.svg';
import NetSaleSVG from '../../components/svgs/NetSale.svg';
import UnitSoldSVG from '../../components/svgs/UnitsSold.svg';
import './paymentReport.scss';

const paymentFetchLimit = 10;
const CATEGORY_OPTIONS = [
  { name: 'All Categories', value: '' },
  { name: 'Semen', value: 'Semen' },
  { name: 'Embryo', value: 'Embryo' },
];
const CATEGORY_ID_MAP: Record<string, number> = {
  Semen: 1,
  Embryo: 2,
};

const INITIAL_SUMMARY: PaymentSummary = {
  grossSales: 0,
  netProfit: 0,
  unitsSold: 0,
};

const PAYMENT_REPORT_INCLUDES = 'MarketPlacePaymentTransaction,InventoryTransaction.Specimen.Animal,InventoryTransaction.Specimen.EmbryoStraws,SellerAccount';

const isFemaleSpecimen = (specimen: MarketPlaceSpecimenResponse): boolean => {
  const gender = specimen.animal?.gender ?? specimen.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

const mapToPaymentReportItem = (item: any): PaymentReportItem => ({
  transactionId: item.inventoryTransactionId,
  animalName: `${
    isFemaleSpecimen(item.inventoryTransaction?.specimen)
      ? item.inventoryTransaction?.specimen?.embryoStraws?.[0]?.embryoStrawCode
      : item.inventoryTransaction?.specimenId
  } - ${item.inventoryTransaction?.specimen?.animal?.name ?? '-'}`,
  buyerName: item.inventoryTransaction?.account?.name ?? '-',
  buyerEmail: item.inventoryTransaction?.account?.contactEmail ?? '-',
  buyerPhone: item.inventoryTransaction?.account?.contactPhone ?? '-',
  category: item.inventoryTransaction?.specimen?.specimenTypeId === 1 ? 'Semen' : 'Embryo',
  units: item.inventoryTransaction?.changeQuantity ?? 0,
  grossAmountCents: (item.sellerAmountCents ?? 0) + (item.rsgCommissionCents ?? 0),
  netAmountCents: item.sellerAmountCents ?? 0,
  commissionCents: item.rsgCommissionCents ?? 0,
  commissionRate: item.marketPlacePaymentTransaction?.commissionPercentage ?? 0,
  saleDate: item.marketPlacePaymentTransaction?.billedDate ?? item.createdDatetime,
});

const withAccountFilter = (accountId: number, filter?: string): string => {
  const accountFilter = `accountId eq ${accountId}`;
  return filter ? `${accountFilter} and ${filter}` : accountFilter;
};

const PaymentReport = ({ accountId }: PaymentReportProps): JSX.Element => {
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [rows, setRows] = useState<PaymentReportItem[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>(INITIAL_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<PaymentReportItem | null>(null);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const isMobileViewRef = useRef(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callNumber: MutableRefObject<number> = useRef(0);

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const isLoadingAdditionalPage: MutableRefObject<boolean> = useRef(false);
  const hasAllResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 767;
      setIsMobileView(mobile);
      isMobileViewRef.current = mobile;
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const paymentReportFilter = useMemo((): string | undefined => {
    const conditions: string[] = [];
    const dateCondition = dateFilter(!!(dateRange.startDate && dateRange.endDate), {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
    if (dateCondition) conditions.push(dateCondition);
    const filteredProduct = debouncedSearch.trim().toLowerCase().replace(/'/g, "''");
    if (filteredProduct) {
      conditions.push(`contains(tolower(inventoryTransaction/specimen/animal/name), '${filteredProduct}')`);
    }
    if (CATEGORY_ID_MAP[category]) {
      conditions.push(`inventoryTransaction/specimen/specimenTypeId eq ${CATEGORY_ID_MAP[category]}`);
    }
    return conditions.length > 0 ? conditions.join(' and ') : undefined;
  }, [dateRange, debouncedSearch, category]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await getMarketPlacePaymentReportSummary(
        accountId,
        StandardParamsToODataParams({
          filter: withAccountFilter(accountId, paymentReportFilter),
        }),
      );
      setSummary({
        grossSales: Number(centsToDollars(res.data?.grossSalesCents ?? 0)),
        netProfit: Number(centsToDollars(res.data?.netProfitCents ?? 0)),
        unitsSold: res.data?.unitsSold ?? 0,
      });
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [accountId, paymentReportFilter]);

  const loadData = useCallback(
    async (offset: number) => {
      if (hasAllResults.current) return;
      callNumber.current++;
      const callNum = callNumber.current;
      try {
        isLoadingRef.current = true;
        setLoading(true);
        const res = await getMarketPlacePaymentReportTransactions(
          StandardParamsToODataParams({
            filter: withAccountFilter(accountId, paymentReportFilter),
            limit: paymentFetchLimit,
            offset,
            include: PAYMENT_REPORT_INCLUDES,
          }),
        );
        if (callNum !== callNumber.current) return;
        const raw: any[] = res.data?.items ?? res.data ?? [];
        const mapped = Array.isArray(raw) ? raw.map(mapToPaymentReportItem) : [];
        hasAllResults.current = raw.length < paymentFetchLimit;
        setRows(prev => (offset === 0 ? mapped : [...prev, ...mapped]));
        setHasMore(raw.length === paymentFetchLimit);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (!hasAllResults.current) {
          isLoadingAdditionalPage.current = false;
        }
      }
    },
    [accountId, paymentReportFilter],
  );

  const handleScroll = useCallback(() => {
    if (hasAllResults.current || isLoadingAdditionalPage.current || isLoadingRef.current) return;

    const scrollTop = window.scrollY;
    const clientHeight = window.innerHeight;
    const scrollHeight = document.documentElement.scrollHeight;

    if (scrollTop + clientHeight >= scrollHeight - 150) {
      isLoadingAdditionalPage.current = true;
      setOffset(prev => prev + paymentFetchLimit);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchmove', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [productSearch]);

  const getData = useCallback(
    (params: StandardParams) =>
      getMarketPlacePaymentReportTransactions(
        StandardParamsToODataParams({
          ...params,
          filter: withAccountFilter(accountId, paymentReportFilter),
          include: PAYMENT_REPORT_INCLUDES,
        }),
      ).then(res => ({
        ...res,
        data: (res.data?.items ?? res.data ?? []).map(mapToPaymentReportItem),
      })),
    [accountId, paymentReportFilter],
  );

  const handleOpenDetail = useCallback((item: PaymentReportItem) => {
    setSelectedDetail(item);
  }, []);

  const buildRow = useCallback(
    (item: PaymentReportItem) => [
      item.transactionId,
      item.animalName ?? '-',
      item.buyerName ?? '-',
      item.category,
      item.units,
      <span key={`gross-${item.transactionId}`}>${centsToDollars(item.grossAmountCents)}</span>,
      <span key={`net-${item.transactionId}`}>${centsToDollars(item.netAmountCents)}</span>,
      <span key={`commission-${item.transactionId}`} className="payment-report__commission">
        ${centsToDollars(item.commissionCents)}
        <span className="payment-report__commission-rate">({item.commissionRate ?? 0}%)</span>
      </span>,
      item.saleDate ? new Date(item.saleDate).toLocaleDateString() : '-',
      <ActionMenu
        actionButtons={[{ name: `${BUTTON_CONSTANTS.VIEW_DETAIL}`, action: () => handleOpenDetail(item) }]}
        key={`action-${item.transactionId}`}
      />,
    ],
    [handleOpenDetail],
  );

  useEffect(() => {
    setRefresh(prev => prev + 1);
    if (isMobileViewRef.current) {
      hasAllResults.current = false;
      isLoadingAdditionalPage.current = false;
      setRows([]);
      setOffset(0);
      void loadData(0);
    }
  }, [paymentReportFilter]);

  useEffect(() => {
    if (!isMobileView) return;
    hasAllResults.current = false;
    isLoadingAdditionalPage.current = false;
    setRows([]);
    setOffset(0);
    void loadData(0);
  }, [isMobileView]);

  useEffect(() => {
    if (!isMobileView || offset === 0) return;
    void loadData(offset);
  }, [offset]);

  const handleClearSearch = () => {
    setProductSearch('');
    setDebouncedSearch('');
  };

  return (
    <>
      <PaymentReportDetailsModal
        isOpen={selectedDetail != null}
        onClose={() => setSelectedDetail(null)}
        paymentDetail={selectedDetail}
      />

      <div className="payment-report">
        <div className="payment-report__filters">
          <div className="payment-report__filter-group">
            <label className="payment-report__filter-label">{LABEL_CONSTANTS.SELECT_DATES}</label>
            <DateRangeInput value={dateRange} onChange={setDateRange} isClearable maxDate={new Date()} />
          </div>

          <div className="payment-report__filter-group payment-report__filter-group--search">
            <label className="payment-report__filter-label">{TABLE_HEADER_CONSTANTS.PRODUCT_NAME}</label>
            <div className="payment-report__search-wrap">
              <input
                type="text"
                className="payment-report__search-input"
                value={productSearch}
                onChange={event => setProductSearch(event.target.value)}
                placeholder="Search Products..."
              />
              {productSearch ? (
                <span className="payment-report__clear-icon" onClick={handleClearSearch}>
                  <CancelSVG />
                </span>
              ) : (
                <span className="payment-report__search-icon">
                  <SearchSVG />
                </span>
              )}
            </div>
          </div>

          <div className="payment-report__filter-group">
            <label className="payment-report__filter-label">{TABLE_HEADER_CONSTANTS.CATEGORY}</label>
            <FilterDropdown
              options={CATEGORY_OPTIONS}
              value={CATEGORY_OPTIONS.find(opt => opt.value === category) ?? CATEGORY_OPTIONS[0]}
              onChange={e => setCategory(e.value)}
              placeholder="All Categories"
              showSearch={true}
            />
          </div>
        </div>

        <div className="payment-report__summary-cards">
          <div className="payment-report__summary-card">
            <span className="payment-report__summary-icon">
              <GrossSaleSVG />
            </span>
            <p className="payment-report__summary-label">{TABLE_HEADER_CONSTANTS.GROSS_SALES}</p>
            <div className="payment-report__summary-value">${(summary.grossSales ?? 0).toFixed(2)}</div>
            <span className="payment-report__summary-sub">
              {LABEL_CONSTANTS.FROM} {TABLE_HEADER_CONSTANTS.ORDERS}
            </span>
          </div>

          <div className="payment-report__summary-card">
            <span className="payment-report__summary-icon">
              <NetSaleSVG />
            </span>
            <p className="payment-report__summary-label">{TABLE_HEADER_CONSTANTS.NET_SALE}</p>
            <div className="payment-report__summary-value payment-report__summary-value--profit">
              ${(summary.netProfit ?? 0).toFixed(2)}
            </div>
            <span className="payment-report__summary-sub">Your earnings</span>
          </div>

          <div className="payment-report__summary-card">
            <span className="payment-report__summary-icon">
              <UnitSoldSVG />
            </span>
            <p className="payment-report__summary-label">{TABLE_HEADER_CONSTANTS.UNITS_SOLD}</p>
            <div className="payment-report__summary-value">{summary.unitsSold}</div>
            <span className="payment-report__summary-sub">{LABEL_CONSTANTS.TOTAL_UNITS}</span>
          </div>
        </div>

        <div className="payment-report__table-header">
          <h4 className="payment-report__table-title">{TABLE_HEADER_CONSTANTS.TRANSACTION_DETAILS}</h4>
        </div>

        <div className="desk-payment-report">
          <PagedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.ORDER_ID, sortValue: 'inventoryTransactionId' },
              { displayName: MARKETPLACE_MY_ORDERS_LABELS.PRODUCT },
              { displayName: TABLE_HEADER_CONSTANTS.BUYER },
              { displayName: TABLE_HEADER_CONSTANTS.CATEGORY },
              { displayName: LABEL_CONSTANTS.UNITS },
              { displayName: TABLE_HEADER_CONSTANTS.GROSS_AMOUNT },
              { displayName: TABLE_HEADER_CONSTANTS.NET_AMOUNT, sortValue: 'sellerAmountCents' },
              { displayName: TABLE_HEADER_CONSTANTS.COMMISSION, sortValue: 'rsgCommissionCents' },
              { displayName: TABLE_HEADER_CONSTANTS.SALE_DATE, sortValue: 'createdDatetime' },
              { displayName: TABLE_HEADER_CONSTANTS.ACTION },
            ]}
            getData={getData}
            buildRow={buildRow}
            sortBy="createdDatetime"
            sortDescByDefault
            filter={paymentReportFilter}
            height={500}
            pageSize={paymentFetchLimit}
            scrollBufferThickness={100}
            refresh={refresh}
            setRefresh={setRefresh}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
          />
        </div>

        <div className="xs-payment-report">
          {!loading && rows.length === 0 ? (
            <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            <>
              {rows.map((transaction, index) => (
                <div className="card max-width" key={`transaction-${transaction.transactionId ?? index}`}>
                  <div className="payment-report-card">
                    <div className="payment-report-card__content">
                      <div className="payment-report-card__info">
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.ORDER_ID} :</label>
                          <label className="payment-report__order-id">{transaction.transactionId}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{MARKETPLACE_MY_ORDERS_LABELS.PRODUCT} :</label>
                          <label>{transaction.animalName ?? '-'}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.BUYER} :</label>
                          <label>{transaction.buyerName ?? '-'}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.CATEGORY} :</label>
                          <label>{transaction.category ?? '-'}</label>
                        </div>
                      </div>
                      <div className="payment-report-card__info">
                        <div className="payment-report-card__cell">
                          <label>{LABEL_CONSTANTS.UNITS} :</label>
                          <label>{transaction.units ?? '-'}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.GROSS_AMOUNT} :</label>
                          <label>${centsToDollars(transaction.grossAmountCents)}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.NET_AMOUNT} :</label>
                          <label>${centsToDollars(transaction.netAmountCents)}</label>
                        </div>
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.COMMISSION} :</label>
                          <label className="payment-report__commission">
                            ${centsToDollars(transaction.commissionCents)}
                            <span className="payment-report__commission-rate">({transaction.commissionRate ?? 0}%)</span>
                          </label>
                        </div>
                      </div>
                      <div className="payment-report-card__info">
                        <div className="payment-report-card__cell">
                          <ActionMenu
                            actionButtons={[
                              { name: `${BUTTON_CONSTANTS.VIEW_DETAIL}`, action: () => handleOpenDetail(transaction) },
                            ]}
                          />
                        </div>
                      </div>
                      <div className="payment-report-card__info">
                        <div className="payment-report-card__cell">
                          <label>{TABLE_HEADER_CONSTANTS.SALE_DATE} :</label>
                          <label>{transaction.saleDate ? new Date(transaction.saleDate).toLocaleDateString() : '-'}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {hasMore && <Loader addedSpace loaderSize="small" />}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentReport;
